/**
 * Gates in-app experiments (as opposed to `exploring/`'s standalone ones) to
 * a short allowlist of real accounts, rather than every signed-in user.
 *
 * Deliberately client-side only, and that's a considered choice, not an
 * oversight: an experiment gated here has no API route of its own and never
 * sends anything to the backend — MediaPipe's own face-landmark detection
 * (the first user of this) runs entirely in the browser, so there is no
 * request to check server-side in the first place. A non-tester who guesses
 * the URL sees an unfinished internal tool and nothing else — no other
 * user's data, nothing written anywhere. If a future experiment under this
 * gate ever *does* call the API, that endpoint needs its own real
 * server-side check; this list existing is not a substitute for one.
 *
 * In LOCAL DEVELOPMENT the allowlist is bypassed for any signed-in
 * account — see the comment on the `import.meta.env.DEV` branch below for
 * why, and for why that bypass provably cannot reach production.
 */
const TESTER_EMAILS = ['felipekalinoskif@gmail.com'];

export function isTester(email: string | null | undefined): boolean {
  // Still requires SOMEONE to be signed in, in dev as much as in
  // production — this opens the experiments to any local account, not to
  // anonymous visitors.
  if (!email) return false;

  // Local development: any signed-in account, including the dev-only
  // "sign in as anyone by name" quick login. The allowlist is a real
  // obstacle to local work rather than a safeguard: Google sign-in only
  // works on the host that `API_ORIGIN` points at (its OAuth `state`
  // cookie is host-only — see the note beside API_ORIGIN in
  // `.env.example`), so testing on any other local host, or from a phone
  // on the LAN, means signing in as a throwaway name and therefore losing
  // access to the very screens being worked on.
  //
  // `import.meta.env.DEV` is resolved by Vite AT BUILD TIME — literally
  // `false` in a production bundle, so this branch is dead code that gets
  // stripped and CANNOT be reached on 2podium.com. That is the whole
  // reason for using it rather than a runtime hostname check, which would
  // ship the bypass to production and merely hide it behind a string
  // comparison anyone could spoof.
  if (import.meta.env.DEV) return true;

  return TESTER_EMAILS.includes(email.trim().toLowerCase());
}
